'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  CreditCard,
  MessageSquare,
  Clock,
  Zap,
  Lock,
  ArrowRight,
  AlertCircle,
  HelpCircle
} from 'lucide-react';
import { SettingsSidebar } from '@/components/dashboard/settings/SettingsSidebar';

interface SubscriptionData {
  plan: 'EMPRENDIMIENTO' | 'NEGOCIO_IA';
  active: boolean;
  trialDaysRemaining: number;
  whatsappCreditsIncluded: number;
  whatsappCreditsUsed: number;
  whatsappExtraCredits: number;
  renewalDate: string;
}

export default function SubscriptionSettingsPage() {
  const [subData, setSubData] = useState<SubscriptionData>({
    plan: 'EMPRENDIMIENTO',
    active: true,
    trialDaysRemaining: 11,
    whatsappCreditsIncluded: 200,
    whatsappCreditsUsed: 42,
    whatsappExtraCredits: 0,
    renewalDate: '2026-11-01'
  });

  const [checkoutModalOpen, setCheckoutModalOpen] = useState<boolean>(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState<'EMPRENDIMIENTO' | 'NEGOCIO_IA' | 'CREDITS'>('NEGOCIO_IA');
  const [selectedCreditAmount, setSelectedCreditAmount] = useState<number>(300);

  // Formulario de tarjeta
  const [cardNumber, setCardNumber] = useState<string>('4509 •••• •••• 8812');
  const [cardExpiry, setCardExpiry] = useState<string>('08/28');
  const [cardCvv, setCardCvv] = useState<string>('•••');
  const [cardHolder, setCardHolder] = useState<string>('JUAN IGNACIO ROJAS');
  const [cardDni, setCardDni] = useState<string>('36894120');

  const [processing, setProcessing] = useState<boolean>(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/subscription')
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.subscription) {
          setSubData(data.subscription);
        }
      })
      .catch((e) => console.warn('Error loading subscription:', e));
  }, []);

  const handleConfirmCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);
    setFeedbackMessage(null);

    try {
      let body: any = {};
      if (selectedUpgradePlan === 'CREDITS') {
        body = { addCredits: selectedCreditAmount };
      } else {
        body = { plan: selectedUpgradePlan };
      }

      const res = await fetch('/api/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        if (body.plan) {
          setSubData((prev) => ({ ...prev, plan: body.plan }));
          setFeedbackMessage(`¡Plan actualizado con éxito a ${body.plan === 'NEGOCIO_IA' ? 'Negocio IA' : 'Emprendimiento'}!`);
        } else if (body.addCredits) {
          setSubData((prev) => ({
            ...prev,
            whatsappExtraCredits: prev.whatsappExtraCredits + body.addCredits
          }));
          setFeedbackMessage(`¡Se acreditaron ${body.addCredits} mensajes adicionales de WhatsApp!`);
        }
        setTimeout(() => {
          setCheckoutModalOpen(false);
          setFeedbackMessage(null);
        }, 1500);
      } else {
        alert(data.error || 'Error en el checkout');
      }
    } catch (err: any) {
      alert(err.message || 'Error de conexión');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Cabecera */}
      <div className="space-y-1">
        <Link
          href="/dashboard/settings"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Volver a Configuración</span>
        </Link>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            Cuenta y plan
          </h1>
          <p className="text-sm text-slate-500">
            Gestioná tu suscripción a AppGenda, tu plan contratado y paquetes de mensajes de WhatsApp.
          </p>
        </div>
      </div>

      {/* Grid con Sidebar Unificado */}
      <div className="grid grid-cols-12 gap-6 items-start">
        {/* Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <SettingsSidebar currentPath="/dashboard/settings/subscription" />
        </div>

        {/* Contenido Principal */}
        <div className="col-span-12 lg:col-span-9 space-y-6">
          {/* Banner de Prueba Gratis / Estado de Suscripción */}
          <div className="bg-gradient-to-r from-emerald-900 to-slate-900 text-white rounded-3xl p-6 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-5 border border-emerald-800/50">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-[11px] font-bold">
                <Clock className="w-3.5 h-3.5" />
                <span>Prueba gratuita activa · Te quedan {subData.trialDaysRemaining} días</span>
              </div>
              <h2 className="text-lg font-bold">
                Plan actual: {subData.plan === 'NEGOCIO_IA' ? 'Negocio IA' : 'Emprendimiento'}
              </h2>
              <p className="text-xs text-slate-300 max-w-xl leading-relaxed">
                Durante el período de prueba tenés acceso completo a todas las prestaciones. Podés cambiar de plan o cancelar en cualquier momento sin permanencia.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-xs rounded-2xl p-4 border border-white/10 text-right shrink-0">
              <span className="text-[10px] text-slate-300 block uppercase font-bold">Próxima renovación</span>
              <span className="text-sm font-bold text-white">{subData.renewalDate}</span>
              <span className="text-[11px] text-emerald-300 block mt-0.5 font-medium">Facturación mensual</span>
            </div>
          </div>

          {/* Comparación de los 2 Planes de AppGenda */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* PLAN EMPRENDIMIENTO */}
            <div
              className={`bg-white rounded-3xl p-6 sm:p-7 shadow-sm border transition-all flex flex-col justify-between space-y-6 ${
                subData.plan === 'EMPRENDIMIENTO'
                  ? 'border-emerald-600 ring-2 ring-emerald-600/10'
                  : 'border-slate-200/90'
              }`}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                    Plan Base
                  </span>
                  {subData.plan === 'EMPRENDIMIENTO' && (
                    <span className="text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                      ✓ Tu plan actual
                    </span>
                  )}
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900">Emprendimiento</h3>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-3xl font-extrabold text-slate-900">$14.500</span>
                    <span className="text-xs text-slate-500">/ mes (promo 1er mes)</span>
                  </div>
                  <span className="text-[11px] text-slate-400 block mt-0.5">Luego $24.500 / mes regular</span>
                </div>

                <div className="pt-2 border-t border-slate-100 space-y-2.5 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Profesionales y servicios ilimitados</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Web de reservas pública 24/7 con branding</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Cobro de señas con Mercado Pago</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>200 recordatorios WhatsApp de cortesía mensual</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Lock className="w-4 h-4 text-slate-300 shrink-0" />
                    <span>Usuarios y PIN de mostrador (restringido)</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Lock className="w-4 h-4 text-slate-300 shrink-0" />
                    <span>Recepcionista IA "Aura" (no incluido)</span>
                  </div>
                </div>
              </div>

              <div>
                {subData.plan === 'EMPRENDIMIENTO' ? (
                  <button
                    disabled
                    type="button"
                    className="w-full py-3 rounded-2xl bg-slate-100 text-slate-500 text-xs font-bold cursor-default"
                  >
                    Plan Activo
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUpgradePlan('EMPRENDIMIENTO');
                      setCheckoutModalOpen(true);
                    }}
                    className="w-full py-3 rounded-2xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 text-xs font-bold transition-all cursor-pointer"
                  >
                    Cambiar a Plan Emprendimiento
                  </button>
                )}
              </div>
            </div>

            {/* PLAN NEGOCIO IA */}
            <div
              className={`bg-white rounded-3xl p-6 sm:p-7 shadow-sm border transition-all flex flex-col justify-between space-y-6 relative overflow-hidden ${
                subData.plan === 'NEGOCIO_IA'
                  ? 'border-emerald-600 ring-2 ring-emerald-600/10'
                  : 'border-purple-300/80'
              }`}
            >
              {/* Badge destacado */}
              <div className="absolute top-0 right-0 bg-purple-600 text-white text-[10px] font-bold px-4 py-1 rounded-bl-2xl">
                RECOMENDADO
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full">
                    Completo + IA
                  </span>
                  {subData.plan === 'NEGOCIO_IA' && (
                    <span className="text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                      ✓ Tu plan actual
                    </span>
                  )}
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    Negocio IA
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </h3>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-3xl font-extrabold text-slate-900">$69.000</span>
                    <span className="text-xs text-slate-500">/ mes</span>
                  </div>
                  <span className="text-[11px] text-slate-400 block mt-0.5">Todo incluido sin límites</span>
                </div>

                <div className="pt-2 border-t border-slate-100 space-y-2.5 text-xs text-slate-700">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Todo lo del Plan Emprendimiento</span>
                  </div>
                  <div className="flex items-center gap-2 font-semibold text-purple-900 bg-purple-50/60 p-2 rounded-xl">
                    <Sparkles className="w-4 h-4 text-purple-600 shrink-0" />
                    <span>Aura IA: Atiende WhatsApp, responde dudas y agenda turnos 24/7</span>
                  </div>
                  <div className="flex items-center gap-2 font-semibold text-purple-900 bg-purple-50/60 p-2 rounded-xl">
                    <Lock className="w-4 h-4 text-purple-600 shrink-0" />
                    <span>Usuarios y PIN de mostrador: oculta caja y comisiones al personal</span>
                  </div>
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>Mensajes automáticos de WhatsApp ilimitados</span>
                  </div>
                </div>
              </div>

              <div>
                {subData.plan === 'NEGOCIO_IA' ? (
                  <button
                    disabled
                    type="button"
                    className="w-full py-3 rounded-2xl bg-slate-100 text-slate-500 text-xs font-bold cursor-default"
                  >
                    Plan Activo
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUpgradePlan('NEGOCIO_IA');
                      setCheckoutModalOpen(true);
                    }}
                    className="w-full py-3 rounded-2xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all shadow-sm cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>Mejorar a Negocio IA</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Bolsa de Créditos de WhatsApp Prepago */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-7 shadow-sm space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-600" />
                  Bolsa de Mensajes de WhatsApp
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Cada mes recibís 200 recordatorios de cortesía incluidos. Si necesitás más, podés cargar paquetes prepagos que nunca vencen.
                </p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200/90 rounded-2xl p-3 px-4 text-right shrink-0">
                <span className="text-[10px] text-emerald-800 uppercase font-bold block">Mensajes consumidos este mes</span>
                <span className="text-lg font-extrabold text-emerald-950">
                  {subData.whatsappCreditsUsed} / {subData.whatsappCreditsIncluded + subData.whatsappExtraCredits}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              {[
                { amount: 100, price: 19500, unit: 195 },
                { amount: 300, price: 52500, unit: 175 },
                { amount: 500, price: 80000, unit: 160 },
                { amount: 1000, price: 150000, unit: 150 },
              ].map((pkg) => (
                <div
                  key={pkg.amount}
                  className="p-4 rounded-2xl border border-slate-200 hover:border-emerald-500 bg-slate-50/50 hover:bg-emerald-50/30 transition-all flex flex-col justify-between space-y-3 text-center"
                >
                  <div>
                    <span className="text-lg font-extrabold text-slate-900 block">{pkg.amount} msgs</span>
                    <span className="text-xs font-bold text-emerald-700 block">${pkg.price.toLocaleString('es-AR')}</span>
                    <span className="text-[10px] text-slate-400 block">${pkg.unit} por mensaje</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedUpgradePlan('CREDITS');
                      setSelectedCreditAmount(pkg.amount);
                      setCheckoutModalOpen(true);
                    }}
                    className="w-full py-1.5 rounded-xl bg-white border border-slate-300 hover:bg-emerald-800 hover:text-white hover:border-emerald-800 text-[11px] font-bold text-slate-700 transition-colors cursor-pointer"
                  >
                    Recargar
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL CHECKOUT MERCADO PAGO (SANDBOX TRANSPARENTE) */}
      {checkoutModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl space-y-5 border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  {selectedUpgradePlan === 'CREDITS'
                    ? `Carga de ${selectedCreditAmount} mensajes de WhatsApp`
                    : `Suscripción ${selectedUpgradePlan === 'NEGOCIO_IA' ? 'Negocio IA' : 'Emprendimiento'}`}
                </h3>
                <span className="text-xs text-slate-400">Procesado con tarjeta vía Mercado Pago</span>
              </div>
              <button
                type="button"
                onClick={() => setCheckoutModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs"
              >
                ✕
              </button>
            </div>

            {/* Aviso de Modo Demostración */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3.5 flex items-start gap-3 text-blue-900 text-xs">
              <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="font-bold text-blue-950">Demostración interactiva segura</span>
                <p>
                  Esta pasarela actualiza de forma real tu plan y capacidades en PostgreSQL sin ejecutar débitos bancarios reales sobre tu tarjeta.
                </p>
              </div>
            </div>

            {feedbackMessage ? (
              <div className="p-6 text-center space-y-3">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto animate-bounce" />
                <p className="text-sm font-bold text-slate-900">{feedbackMessage}</p>
              </div>
            ) : (
              <form onSubmit={handleConfirmCheckout} className="space-y-4 text-xs">
                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Número de tarjeta</label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 font-mono focus:bg-white focus:outline-emerald-600 pl-10"
                    />
                    <CreditCard className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="font-bold text-slate-700 block">Vencimiento (MM/AA)</label>
                    <input
                      type="text"
                      required
                      value={cardExpiry}
                      onChange={(e) => setCardExpiry(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 font-mono focus:bg-white focus:outline-emerald-600"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-bold text-slate-700 block">Código de seguridad (CVV)</label>
                    <input
                      type="text"
                      required
                      value={cardCvv}
                      onChange={(e) => setCardCvv(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 font-mono focus:bg-white focus:outline-emerald-600"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Nombre del titular</label>
                  <input
                    type="text"
                    required
                    value={cardHolder}
                    onChange={(e) => setCardHolder(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 uppercase focus:bg-white focus:outline-emerald-600"
                  />
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">DNI del titular</label>
                  <input
                    type="text"
                    required
                    value={cardDni}
                    onChange={(e) => setCardDni(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 font-mono focus:bg-white focus:outline-emerald-600"
                  />
                </div>

                <div className="pt-2 flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => setCheckoutModalOpen(false)}
                    className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-800"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2.5 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold transition-all shadow-sm cursor-pointer disabled:opacity-50"
                  >
                    {processing ? 'Confirmando...' : 'Confirmar Suscripción'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
